"use client";

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import Link from "next/link";
import {
  IconAlertCircle,
  IconArrowRight,
  IconArrowUp,
  IconCheck,
  IconChevronRight,
  IconCircleCheck,
  IconCircleX,
  IconFileText,
  IconLink,
  IconMail,
  IconPhone,
  IconShieldLock,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type {
  LegalPageContent,
  LegalContentBlock,
} from "@/content/legal/types";

interface LegalPageRendererProps {
  content: LegalPageContent;
  /**
   * @deprecated Ignored. The renderer paints with the theme's semantic tokens,
   * so it follows the site's light/dark palette on its own. Kept so existing
   * pages still compile; drop it from the call site.
   */
  variant?: "light" | "dark";
  /**
   * @deprecated Ignored. The document reads as one centered column with no
   * section navigation.
   */
  showTopNav?: boolean;
}

/** "1. Operator and contact" -> "Operator and contact" */
const displayTitle = (title: string) => title.replace(/^\d+\.\s*/, "").trim();

const pad = (n: number) => String(n).padStart(2, "0");

/** The two policy documents, switchable from the header and the contact panel. */
const DOCS = [
  { label: "Terms of Service", href: "/terms", blurb: "Accounts, use, billing, refunds", icon: IconFileText },
  { label: "Privacy Policy", href: "/privacy", blurb: "Data, retention and your rights", icon: IconShieldLock },
] as const;

/** Permalinks inside the Terms the footer also lists (ids are stable, see terms-of-service.ts). */
const QUICK_LINKS = [
  { label: "Delivery", href: "/terms#delivery-and-availability" },
  { label: "Refunds", href: "/terms#refunds" },
  { label: "Disputes", href: "/terms#dispute-resolution" },
] as const;

/** `tel:` target: digits and a leading plus only. */
const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, "")}`;

const blockWords = (block: LegalContentBlock) => {
  const text =
    block.type === "list"
      ? block.items.join(" ")
      : block.type === "definition"
        ? `${block.term} ${block.definition}`
        : block.text;
  return text.split(/\s+/).filter(Boolean).length;
};

const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Renders a legal page (Terms, Privacy) as the rulebook: a compact header
 * with the document's date and version, the full text on one calm,
 * centered sheet with every provision visible, and a contact
 * panel with the related documents. Colors come only from theme tokens.
 */
export function LegalPageRenderer({ content }: LegalPageRendererProps) {
  const [activeSection, setActiveSection] = useState<string>("");
  const [progress, setProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const articleRef = useRef<HTMLElement>(null);
  const phone = content.supportPhone || null;

  const sections = useMemo(() => {
    const seenIds = new Set<string>();
    const seenTitles = new Set<string>();

    return content.sections.filter((section) => {
      const title = displayTitle(section.title).toLowerCase();
      if (seenIds.has(section.id) || seenTitles.has(title)) return false;
      seenIds.add(section.id);
      seenTitles.add(title);
      return true;
    });
  }, [content.sections]);

  const readMinutes = useMemo(() => {
    const words = sections.reduce(
      (sum, section) => sum + section.content.reduce((n, block) => n + blockWords(block), 0),
      0,
    );
    return Math.max(1, Math.round(words / 220));
  }, [sections]);

  const currentDoc = DOCS.find((doc) => doc.label === content.title);

  // Active section, reading progress through the document, back-to-top.
  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      setShowBackToTop(window.scrollY > 500);

      const article = articleRef.current;
      if (article) {
        const rect = article.getBoundingClientRect();
        const span = rect.height - window.innerHeight * 0.6;
        const read = (window.innerHeight * 0.4 - rect.top) / (span > 0 ? span : 1);
        setProgress(Math.min(1, Math.max(0, read)));
      }

      const elements = sections
        .map((s) => document.getElementById(s.id))
        .filter((el): el is HTMLElement => el !== null);
      if (elements.length === 0) return;

      let current = 0;
      for (let i = 0; i < elements.length; i++) {
        if (elements[i].getBoundingClientRect().top <= 160) current = i;
        else break;
      }
      // The last short sections can never reach the top: at the end of the page, mark the last one.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        current = elements.length - 1;
      }
      setActiveSection(sections[current]?.id ?? "");
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [sections]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: reduceMotion() ? "auto" : "smooth" });
  };

  const copyLink = async (id: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    window.history.replaceState(null, "", `#${id}`);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard blocked: the address bar now holds the section link instead.
    }
    setCopied(id);
    window.setTimeout(() => setCopied((value) => (value === id ? null : value)), 1800);
  };

  const progressStyle = { "--p": progress.toFixed(3) } as CSSProperties;

  return (
    <div className="relative isolate overflow-hidden bg-background text-foreground">
      {/* atmosphere: lavender light, pixel texture and a few geometric lines, all kept off the text */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="rb-atmos absolute inset-0" />
        <div className="rb-pixels absolute inset-0" />
        <svg
          className="absolute right-0 top-0 hidden h-[420px] w-[46vw] max-w-[720px] md:block"
          viewBox="0 0 720 420"
          fill="none"
          preserveAspectRatio="xMaxYMin meet"
        >
          <path d="M720 60H420L360 120V420" className="rb-geo" strokeWidth="1" />
          <path d="M720 110H450L400 160V420" className="rb-geo" strokeWidth="1" opacity="0.6" />
          <path d="M720 160H480L440 200V420" className="rb-geo" strokeWidth="1" opacity="0.35" />
          <path d="M600 60H420L400 80" className="rb-geo-lime" strokeWidth="1.5" />
          <rect x="356" y="236" width="8" height="8" className="rb-lime" opacity="0.7" />
          <rect x="396" y="300" width="6" height="6" className="rb-lime" opacity="0.4" />
        </svg>
      </div>

      {/* ---------- header ---------- */}
      <header className="mx-auto w-full max-w-[1280px] px-4 pb-8 pt-6 sm:px-8 sm:pt-8 lg:px-12 lg:pb-10">
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
            <li>
              <Link href="/" className="rounded transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden>
              <IconChevronRight className="size-3.5" />
            </li>
            <li>Legal</li>
            <li aria-hidden>
              <IconChevronRight className="size-3.5" />
            </li>
            <li aria-current="page" className="font-medium text-foreground">
              {content.title}
            </li>
          </ol>
        </nav>

        <div className="mt-6 grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-10">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary/80 px-3 py-1 font-mono text-xs uppercase tracking-[0.16em] text-foreground ring-1 ring-foreground/10">
              <span aria-hidden className="h-1.5 w-1.5 rounded-xs bg-primary shadow-[0_0_8px] shadow-primary/60" />
              Legal hub · The rulebook
            </span>
            <h1 className="mt-4 font-heading text-4xl font-bold leading-[1.02] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {content.title}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground/80 md:text-lg">
              {content.description}
            </p>

            {/* document switcher */}
            <nav
              aria-label="Legal documents"
              className="mt-6 inline-flex max-w-full flex-wrap rounded-xl bg-background/60 p-1 ring-1 ring-foreground/10"
            >
              {DOCS.map((doc) => {
                const on = doc === currentDoc;
                return (
                  <Link
                    key={doc.href}
                    href={doc.href}
                    aria-current={on ? "page" : undefined}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors",
                      on ? "bg-primary text-primary-foreground" : "text-foreground/75 hover:bg-foreground/5 hover:text-foreground",
                    )}
                  >
                    <doc.icon className="size-4" aria-hidden />
                    {doc.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* document spec card */}
          <div className="relative lg:col-span-5">
            <div aria-hidden className="rb-bob pointer-events-none absolute -right-2 -top-24 hidden w-32 lg:block xl:-top-28 xl:w-36">
              <RulebookEmblem />
            </div>
            <dl className="rb-panel relative grid grid-cols-2 gap-px overflow-hidden rounded-2xl sm:grid-cols-4 lg:grid-cols-2">
              {[
                { label: "Last updated", value: content.lastUpdated },
                { label: "Version", value: content.version ? `v${content.version}` : "—" },
                { label: "Sections", value: String(sections.length) },
                { label: "Reading time", value: `~${readMinutes} min` },
              ].map((item) => (
                <div key={item.label} className="bg-background/30 px-4 py-3.5">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</dt>
                  <dd className="mt-1 font-heading text-lg font-bold text-foreground">{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </header>

      {/* ---------- document ---------- */}
      <div className="mx-auto w-full max-w-[1280px] px-4 pb-16 sm:px-8 lg:px-12">
        {/* the full text, one centered column: every provision visible */}
        <div className="mx-auto w-full max-w-[940px]">
            <article ref={articleRef} className="rb-paper rounded-2xl px-5 sm:px-10 lg:px-14">
              {sections.map((section, idx) => (
                <section
                  key={section.id}
                  id={section.id}
                  aria-labelledby={`${section.id}-title`}
                  className={cn("scroll-mt-28 py-8 sm:py-10", idx > 0 && "border-t border-foreground/10")}
                >
                  <div>
                    <header className="group flex items-start gap-3 sm:gap-4">
                      <span
                        aria-hidden
                        className={cn(
                          "rb-chip mt-0.5 grid h-8 w-10 shrink-0 place-items-center font-mono text-sm font-bold tabular-nums transition-colors",
                          activeSection === section.id && "rb-chip-on",
                        )}
                      >
                        {pad(idx + 1)}
                      </span>
                      <h2
                        id={`${section.id}-title`}
                        className="min-w-0 flex-1 font-heading text-xl font-bold leading-tight tracking-tight text-foreground sm:text-2xl"
                      >
                        <span className="sr-only">{idx + 1}. </span>
                        {displayTitle(section.title)}
                      </h2>
                      <button
                        type="button"
                        onClick={() => copyLink(section.id)}
                        aria-label={`Copy link to “${displayTitle(section.title)}”`}
                        className={cn(
                          "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-xs font-medium outline-none ring-1 transition-[color,opacity,box-shadow] focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring",
                          copied === section.id
                            ? "text-primary ring-primary/40"
                            : "text-muted-foreground ring-foreground/10 hover:text-foreground hover:ring-foreground/30 lg:opacity-0 lg:group-hover:opacity-100",
                        )}
                      >
                        {copied === section.id ? (
                          <IconCheck className="size-4" aria-hidden />
                        ) : (
                          <IconLink className="size-4" aria-hidden />
                        )}
                        <span className="hidden sm:inline">{copied === section.id ? "Copied" : "Copy link"}</span>
                      </button>
                    </header>
                    <div className="mt-5 space-y-4 sm:pl-14">
                      {section.content.map((block, blockIdx) => (
                        <ContentBlock key={blockIdx} block={block} />
                      ))}
                    </div>
                  </div>
                </section>
              ))}
            </article>
            <p aria-live="polite" className="sr-only">
              {copied ? "Section link copied" : ""}
            </p>
        </div>

        {/* ---------- contact and related documents ---------- */}
        <section aria-labelledby="rb-contact-title" className="rb-panel mt-10 overflow-hidden rounded-2xl">
          <div className="grid gap-6 p-5 sm:p-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Support</span>
              <h2 id="rb-contact-title" className="mt-1 font-heading text-2xl font-bold tracking-tight text-foreground">
                Questions about this document?
              </h2>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <dt className="w-14 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Email</dt>
                  <dd className="min-w-0 break-all">
                    <a
                      href={`mailto:${content.supportEmail}`}
                      className="font-medium text-foreground underline decoration-foreground/30 underline-offset-4 transition-colors hover:decoration-primary"
                    >
                      {content.supportEmail}
                    </a>
                  </dd>
                </div>
                {phone && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <dt className="w-14 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Phone</dt>
                    <dd>
                      <a
                        href={telHref(phone)}
                        className="font-medium text-foreground underline decoration-foreground/30 underline-offset-4 transition-colors hover:decoration-primary"
                      >
                        {phone}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Button asChild size="lg" className="arcade-press h-11 rounded-xl px-5 font-heading font-bold">
                  <a href={`mailto:${content.supportEmail}`} className="inline-flex items-center gap-2">
                    <IconMail className="size-4" aria-hidden />
                    Email support
                  </a>
                </Button>
                {phone && (
                  <Button asChild variant="outline" size="lg" className="h-11 rounded-xl px-5 font-heading font-bold">
                    <a href={telHref(phone)} className="inline-flex items-center gap-2">
                      <IconPhone className="size-4" aria-hidden />
                      Call us
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <nav aria-label="Related legal documents" className="lg:col-span-7">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Related documents</span>
              <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
                {DOCS.map((doc) => {
                  const on = doc === currentDoc;
                  return (
                    <li key={doc.href}>
                      <Link
                        href={doc.href}
                        aria-current={on ? "page" : undefined}
                        className={cn("rb-doc group flex items-center gap-3 rounded-xl p-3.5", on && "rb-doc-on")}
                      >
                        <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent/15 text-accent ring-1 ring-accent/30">
                          <doc.icon className="size-5" aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-heading text-sm font-bold text-foreground">{doc.label}</span>
                          <span className="block truncate text-xs text-foreground/65">
                            {on ? "You are reading this" : doc.blurb}
                          </span>
                        </span>
                        <IconArrowRight
                          className="size-4 shrink-0 text-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
                          aria-hidden
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs text-muted-foreground">Jump to the Terms:</span>
                {QUICK_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 rounded-full bg-background/50 px-3 py-1 text-xs font-medium text-foreground/80 ring-1 ring-foreground/10 transition-colors hover:text-foreground hover:ring-accent/50"
                  >
                    {link.label}
                    <IconArrowRight className="size-3" aria-hidden />
                  </Link>
                ))}
              </div>
            </nav>
          </div>
        </section>
      </div>

      {/* Back to Top Button, ringed by reading progress */}
      <button
        type="button"
        onClick={scrollToTop}
        style={progressStyle}
        className={cn(
          "rb-top fixed bottom-6 right-6 z-40 grid size-12 place-items-center rounded-full text-foreground transition-[opacity,transform] duration-300 hover:text-primary",
          showBackToTop ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0",
        )}
        aria-label="Back to top"
        tabIndex={showBackToTop ? 0 : -1}
      >
        <IconArrowUp className="size-5" aria-hidden />
      </button>
    </div>
  );
}

/** Stacked rule plates: a small glossy emblem above the header's spec card. */
function RulebookEmblem() {
  const plate = (y: number, top: string) => (
    <g key={y} transform={`translate(0 ${y})`}>
      <polygon points="10,60 110,110 110,124 10,74" fill="url(#rb-em-l)" />
      <polygon points="110,110 210,60 210,74 110,124" fill="url(#rb-em-r)" />
      <polygon points="110,10 210,60 110,110 10,60" fill={`url(#${top})`} />
    </g>
  );
  return (
    <svg viewBox="0 0 220 200" className="rb-emblem h-auto w-full">
      <defs>
        <linearGradient id="rb-em-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="rb-g-hi" />
          <stop offset="1" className="rb-g-lav" />
        </linearGradient>
        <linearGradient id="rb-em-top-b" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="rb-g-lav" />
          <stop offset="1" className="rb-g-deep" />
        </linearGradient>
        <linearGradient id="rb-em-l" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="rb-g-lav" />
          <stop offset="1" className="rb-g-deep" />
        </linearGradient>
        <linearGradient id="rb-em-r" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="rb-g-deep" />
          <stop offset="1" className="rb-g-navy" />
        </linearGradient>
      </defs>
      {plate(56, "rb-em-top-b")}
      {plate(28, "rb-em-top-b")}
      {plate(0, "rb-em-top")}
      {/* rule lines on the top plate, following its slope */}
      <polygon points="64,52 120,24 132,30 76,58" className="rb-ink" />
      <polygon points="80,60 136,32 148,38 92,66" className="rb-ink" opacity="0.8" />
      <polygon points="96,68 136,48 148,54 108,74" className="rb-ink" opacity="0.6" />
      <rect x="150" y="62" width="10" height="10" className="rb-lime" transform="rotate(-27 155 67)" />
    </svg>
  );
}

/**
 * Renders individual content blocks
 */
function ContentBlock({ block }: { block: LegalContentBlock }) {
  const nosnippet = block.nosnippet ? { "data-nosnippet": true } : {};

  switch (block.type) {
    case "paragraph":
      return (
        <p className="text-[15px] leading-[1.75] text-foreground/90 sm:text-base" {...nosnippet}>
          {block.text}
        </p>
      );

    case "subheading":
      return (
        <h3
          className="flex items-center gap-2 pt-2 font-heading text-base font-bold text-foreground sm:text-lg"
          {...nosnippet}
        >
          <span aria-hidden className="h-1.5 w-1.5 rounded-xs bg-accent" />
          {block.text}
        </h3>
      );

    case "list": {
      const cross = block.marker === "cross";
      const Marker = cross ? IconCircleX : IconCircleCheck;
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag className="space-y-2.5" {...nosnippet}>
          {block.items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3 text-[15px] leading-[1.7] text-foreground/90 sm:text-base">
              {block.ordered ? (
                <span className="rb-chip mt-0.5 grid h-5 w-6 shrink-0 place-items-center font-mono text-[10px] font-bold">
                  {idx + 1}
                </span>
              ) : (
                <Marker
                  aria-hidden
                  className={cn("mt-1 size-[18px] shrink-0", cross ? "text-destructive" : "text-primary")}
                />
              )}
              <span className="min-w-0">{item}</span>
            </li>
          ))}
        </ListTag>
      );
    }

    case "highlight":
      return (
        <div className="flex items-start gap-3 rounded-xl bg-primary/10 p-4 ring-1 ring-primary/25" {...nosnippet}>
          <IconAlertCircle className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
          <p className="text-[15px] leading-relaxed text-foreground">{block.text}</p>
        </div>
      );

    case "definition":
      return (
        <dl
          className="grid gap-1 border-b border-foreground/10 pb-3 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] sm:gap-6"
          {...nosnippet}
        >
          <dt className="font-mono text-sm font-medium text-foreground">{block.term}</dt>
          <dd className="text-[15px] leading-relaxed text-foreground/80">{block.definition}</dd>
        </dl>
      );

    default:
      return null;
  }
}
